import { AttributeField } from "./UploadModalAttributeField";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import type { Tag } from "@/services/api/tags/types";
import { TagSearchInput } from "../file-explorer/bulk-add-tags/TagSearchInput";
import { AvailableTagsList } from "../file-explorer/bulk-add-tags/AvailableTagsList";
import { AssignedTagsArea } from "../shared/AssignedTagsArea";

interface AttributePanelProps {
  requiredAttributes: DisplayAttribute[];
  optionalAttributes: DisplayAttribute[];
  categoryAttributes: DisplayAttribute[];
  isLoading: boolean;
  attributeValues: Record<string | number, string | number | undefined>;
  onAttributeChange: (
    attributeId: string | number,
    value: string | number | undefined
  ) => void;
  selectedTags: Tag[];
  filteredAvailableTags: Tag[];
  tagsLoading: boolean;
  tagSearchQuery: string;
  onTagSearchChange: (value: string) => void;
  onTagToggle: (tagId: number) => void;
  hasAvailableTags: boolean;
}

export function AttributePanel({
  requiredAttributes,
  optionalAttributes,
  categoryAttributes,
  isLoading,
  attributeValues,
  onAttributeChange,
  selectedTags,
  filteredAvailableTags,
  tagsLoading,
  tagSearchQuery,
  onTagSearchChange,
  onTagToggle,
  hasAvailableTags,
}: AttributePanelProps) {
  return (
    <div className="flex flex-col w-96 min-w-96 border-r border-grid-200 pr-8 overflow-y-auto">
      <h3 className="text-sm font-semibold text-ink-900 mb-4">
        Attribute Selection
      </h3>
      <div className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-ink-500">Loading attributes...</p>
        ) : (
          <>
            {requiredAttributes.length > 0 && (
              <div className="space-y-4">
                {requiredAttributes.map((attr) => (
                  <AttributeField
                    key={attr.id}
                    attribute={attr}
                    value={attributeValues[attr.id]}
                    isRequired
                    onChange={(value) => onAttributeChange(attr.id, value)}
                  />
                ))}
              </div>
            )}
            {optionalAttributes.length > 0 && (
              <div className="space-y-4 pt-4 border-t border-grid-200">
                {optionalAttributes.map((attr) => (
                  <AttributeField
                    key={attr.id}
                    attribute={attr}
                    value={attributeValues[attr.id]}
                    isRequired={attr.isRequired}
                    onChange={(value) => onAttributeChange(attr.id, value)}
                  />
                ))}
              </div>
            )}
            {categoryAttributes.length === 0 && (
              <p className="text-sm text-ink-500">
                No attributes configured for this category.
              </p>
            )}
          </>
        )}
      </div>

      <div className="space-y-3 pt-6 mt-6 border-t border-grid-200">
        <h3 className="text-sm font-semibold text-ink-900">
          Tags{" "}
          <span className="text-ink-400 font-normal">(optional)</span>
        </h3>
        <AssignedTagsArea
          assignedTags={selectedTags}
          onRemove={(tagId) => onTagToggle(Number(tagId))}
        />
        <TagSearchInput value={tagSearchQuery} onChange={onTagSearchChange} />
        <AvailableTagsList
          tags={filteredAvailableTags}
          onAdd={(tagId) => onTagToggle(Number(tagId))}
          isLoading={tagsLoading}
          searchQuery={tagSearchQuery}
          hasAvailableTags={hasAvailableTags}
        />
      </div>
    </div>
  );
}
